import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { InputLabel, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import { Color } from '~/constants';
import { ALERTDATEFORMAT } from '~/constants/common';
import { useIsMobileDevice } from '~/hooks';
import { I18n } from '~/i18n';
import { useAppDispatch, useAppSelector } from '~/redux-rtk/hooks';
import {
  AlertActions,
  CalendarMenu,
  TimeFilter,
} from '~/redux-rtk/slices/alertSlice';
import { theme } from '~/theme';
import { mergeRefs } from '~/utils/alertsUtils';

const menuItemStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1,
};

export default function PersonalisedPeriod({
  calenderContainer,
  updateCalenderContainer,
  handleClose,
}: {
  calenderContainer: null | HTMLElement;
  updateCalenderContainer: (data: null | HTMLElement) => void;
  handleClose: () => void;
}) {
  const open = Boolean(calenderContainer);

  const calendarSettings = useAppSelector(
    state => state.alert.expiredAlertFilters.time,
  );

  const dispatch = useAppDispatch();
  const [dateRange, setDateRange] = useState<{
    from: Dayjs | null;
    to: Dayjs | null;
  }>({
    from: null,
    to: null,
  });
  const [formErrors, setFormErrors] = useState({ from: '', to: '' });

  const isMobile = useIsMobileDevice();
  const updateFilters = (value: TimeFilter) => {
    dispatch(AlertActions.updateExpiredAlertFilters({ time: value }));
  };

  const fromRef = useRef<HTMLInputElement | null>(null);
  const toRef = useRef<HTMLInputElement | null>(null);
  const applyRef = useRef<HTMLButtonElement | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);

  useEffect(() => {
    if (open) {
      // initally when the component is rendered there is a slight delay when this fromRef becomes valid.
      // hence the timeout, to make sure the input gets focuesed on the intial render
      const timer = setTimeout(() => {
        fromRef.current?.focus();
        setFocusIndex(0);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (calendarSettings.selectedMenu === CalendarMenu.customPeriod) {
      setDateRange({
        from: dayjs(calendarSettings?.fromDate),
        to: dayjs(calendarSettings?.toDate),
      });
    }
  }, [calendarSettings]);

  useEffect(() => {
    setFormErrors({ from: '', to: '' });
  }, [dateRange]);

  const handleApply = () => {
    const newErrors = { from: '', to: '' };

    if (!dateRange.from) newErrors.from = I18n.t('alertsList.required');
    if (!dateRange.to) newErrors.to = I18n.t('alertsList.required');
    if (
      dateRange.to &&
      dateRange.from &&
      dateRange.to.isBefore(dateRange.from)
    ) {
      newErrors.to = I18n.t('alertsList.dateRangeError');
    }

    setFormErrors(newErrors);
    if (newErrors.to || newErrors.from) return;

    updateFilters({
      fromDate: dateRange.from!.format(ALERTDATEFORMAT),
      selectedMenu: CalendarMenu.customPeriod,
      toDate: dateRange.to!.format(ALERTDATEFORMAT),
    });
    updateCalenderContainer(null);
    handleClose();
  };

  const HandleKeyDown = (e: React.KeyboardEvent) => {
    const refs = [fromRef, toRef, applyRef];
    if (e.key === 'ArrowDown' || e.keyCode === 40) {
      const next = (focusIndex + 1) % refs.length;
      refs[next]?.current?.focus();
      setFocusIndex(next);
    }
    if (e.key === 'ArrowUp' || e.keyCode === 38) {
      const prev = (focusIndex - 1 + refs.length) % refs.length;
      refs[prev]?.current?.focus();
      setFocusIndex(prev);
    }
    e.stopPropagation();
  };

  return (
    <>
      <Button
        id="basic-button"
        aria-haspopup="true"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
          updateCalenderContainer(e.currentTarget)
        }
        endIcon={<KeyboardArrowRight sx={{ color: 'black' }} />}
        variant="text"
        sx={{ padding: 0, color: 'black' }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight:
              calendarSettings?.selectedMenu === CalendarMenu.customPeriod
                ? 600
                : 400,
          }}
        >
          {I18n.t('alertsList.customPeriod')}
        </Typography>
      </Button>

      <Menu
        id="menu"
        anchorEl={calenderContainer}
        open={open}
        onClose={() => updateCalenderContainer(null)}
        onKeyDown={HandleKeyDown}
        MenuListProps={{
          sx: {
            minWidth: '200px',
          },
          'aria-labelledby': 'menu-button',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: isMobile ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        sx={{
          maxWidth: '240px',
          '& .MuiPaper-root': {
            border: `1px solid ${Color.Grey200}`,
            borderRadius: '5px',
          },
          '& .MuiList-root': {
            padding: 0,
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MenuItem
            sx={{
              ...menuItemStyles,
              backgroundColor: focusIndex === 0 ? Color.Grey100 : 'transparent',
            }}
          >
            <InputLabel>{I18n.t('alertsList.from')}</InputLabel>
            <DesktopDatePicker
              value={dateRange.from}
              inputFormat="YYYY-MM-DD"
              InputProps={{
                size: 'small',
                sx: {
                  '& .MuiInputBase-root': {
                    minHeight: '30px',
                  },
                },
              }}
              onChange={newValue =>
                setDateRange({ ...dateRange, from: newValue })
              }
              PaperProps={{
                sx: {
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                  border: 'solid 1px rgba(0, 0, 0, 0.15)',
                  borderRadius: '6px',
                  maxHeight: 'clamp(100px, 60vh, 600px)',
                  paddingBottom:"10px",
                  overflowY: 'auto',
                },
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={!!formErrors.from}
                  helperText={formErrors.from}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: '40px',
                      fontSize: 13,
                      fontWeight: 'bold',
                    },
                  }}
                  //if we directly do `inputRef={fromRef}` this will affect MUI's popup anchor location
                  //so merge the MUI's params.inputRef and our custom fromRef
                  inputRef={mergeRefs(params.inputRef, fromRef)}
                />
              )}
            />
          </MenuItem>
          <MenuItem
            sx={{
              ...menuItemStyles,
              backgroundColor: focusIndex === 1 ? Color.Grey100 : 'transparent',
            }}
          >
            <InputLabel>{I18n.t('alertsList.to')}</InputLabel>
            <DesktopDatePicker
              value={dateRange.to}
              inputFormat="YYYY-MM-DD"
              onChange={newValue =>
                setDateRange({ ...dateRange, to: newValue })
              }
              PaperProps={{
                sx: {
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                  border: 'solid 1px rgba(0, 0, 0, 0.15)',
                  borderRadius: '6px',
                  maxHeight: 'clamp(100px, 60vh, 600px)',
                   paddingBottom:"10px",
                  overflowY: 'auto',
                },
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={!!formErrors.to}
                  helperText={formErrors.to}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: '40px',
                      fontSize: 13,
                      fontWeight: 'bold',
                    },
                    '& .MuiFormHelperText-root': {
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      width: '100%',
                      fontSize: '12px',
                    },
                  }}
                  //if we directly do `inputRef={toRef}` this will affect MUI's popup anchor location
                  //so merge the MUI's params.inputRef and our custom toRef
                  inputRef={mergeRefs(params.inputRef, toRef)}
                />
              )}
            />
          </MenuItem>
        </LocalizationProvider>

        <MenuItem
          sx={{
            '&:hover': { backgroundColor: 'white' },
            backgroundColor: focusIndex === 2 ? Color.Grey100 : 'transparent',
          }}
        >
          <Button
            ref={applyRef}
            size="small"
            sx={{
              marginLeft: 'auto',
              fontSize: '14px',
              color: theme.palette.primary.main,
            }}
            onClick={handleApply}
          >
            {I18n.t('alertsList.apply')}
          </Button>
        </MenuItem>
      </Menu>
    </>
  );
}
